import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PLANS, featureOf, money, yearlySavingPercent, TRIAL_DAYS } from '../../config/plans';
import { Check, SoonTag, signupPath } from './shared';
import { usePricing } from '../../lib/pricing';

/** Monthly / yearly switch. */
export function IntervalToggle({ interval, onChange, saving, className = '' }) {
  const opt = (id, label) => (
    <button type="button" role="radio" aria-checked={interval === id} onClick={() => onChange(id)}
      className={`h-10 px-4 rounded-full text-[15px] font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue ${interval === id ? 'bg-navy text-white' : 'text-ink-muted hover:text-ink'}`}>
      {label}
    </button>
  );
  return (
    <div role="radiogroup" aria-label="Billing period" className={`inline-flex items-center p-1 rounded-full bg-white border border-line ${className}`}>
      {opt('month', 'Monthly')}
      {opt('year', saving ? `Yearly, save ${saving}%` : 'Yearly')}
    </div>
  );
}

export function priceLine(plan, interval) {
  const n = plan.price[interval];
  if (!n) return { amount: '$0', unit: 'forever' };
  const unit = `${plan.perSeat ? 'per person / month' : 'per month'}`;
  return { amount: money(n), unit };
}

export function billingNote(plan, interval) {
  if (!plan.price.month) return 'No card needed';
  return TRIAL_DAYS ? `${TRIAL_DAYS}-day free trial. ${paidNote(plan, interval)}` : paidNote(plan, interval);
}

function paidNote(plan, interval) {
  if (interval === 'year') {
    const yearly = plan.yearTotal ?? plan.price.year * 12;
    return plan.perSeat ? `Billed yearly, ${money(yearly)} per person. Minimum ${plan.minSeats} people.` : `Billed yearly, ${money(yearly)} a year`;
  }
  return plan.perSeat ? `Billed monthly. Minimum ${plan.minSeats} people.` : 'Billed monthly';
}

export default function Pricing() {
  usePricing();
  const [interval, setBilling] = useState('year');
  const savings = PLANS.map(yearlySavingPercent).filter(Boolean);
  // Pro and Teams save different amounts yearly: "save up to 29%"
  const saving = Math.min(...savings) === Math.max(...savings) ? Math.max(...savings) : `up to ${Math.max(...savings)}`;
  return (
    <section id="pricing" aria-labelledby="pricing-title" className="py-20 sm:py-28 bg-surface border-t border-line">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-[640px]">
            <h2 id="pricing-title" className="font-display font-extrabold text-[34px] leading-[38px] sm:text-[46px] sm:leading-[50px] tracking-[-0.03em]">
              Start free. Pay when you want the leads.
            </h2>
            <p className="mt-4 text-[18px] leading-[28px] text-ink-muted">Simple plans for one person or a whole team.</p>
          </div>
          <IntervalToggle interval={interval} onChange={setBilling} saving={saving} />
        </div>

        <div className="mt-12 grid lg:grid-cols-3 gap-5 items-stretch">
          {PLANS.map((plan) => {
            const dark = !!plan.highlight;
            const { amount, unit } = priceLine(plan, interval);
            return (
              <article key={plan.id} aria-labelledby={`plan-${plan.id}`}
                className={`rounded-3xl p-7 sm:p-8 flex flex-col ${dark ? 'bg-navy text-white shadow-[0_30px_60px_-30px_rgba(11,21,48,0.6)]' : 'bg-white border border-line'}`}>
                <h3 id={`plan-${plan.id}`} className="font-display font-extrabold text-[28px] leading-8">{plan.name}</h3>
                <p className={`mt-1.5 text-[16px] ${dark ? 'text-[#C9D6E6]' : 'text-ink-muted'}`}>{plan.tagline}</p>
                <p className="mt-7 flex items-baseline gap-2">
                  <span className="font-display font-extrabold text-[56px] leading-none tracking-[-0.03em]">{amount}</span>
                  <span className={`text-[15px] ${dark ? 'text-[#C9D6E6]' : 'text-ink-muted'}`}>{unit}</span>
                </p>
                <p className={`mt-2 text-[14px] min-h-[21px] ${dark ? 'text-[#9FB3CC]' : 'text-ink-muted'}`}>{billingNote(plan, interval)}</p>
                <Link to={signupPath(plan.id, interval)}
                  className={`mt-7 h-12 rounded-xl font-semibold text-[16px] flex items-center justify-center transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${dark ? 'bg-blue text-white hover:bg-blue-dark focus-visible:outline-white' : 'bg-blue text-white hover:bg-blue-dark focus-visible:outline-blue'}`}>
                  {plan.cta}
                </Link>
                <ul className={`mt-8 pt-6 space-y-3 border-t ${dark ? 'border-white/15' : 'border-line'}`}>
                  {plan.features.map((f) => {
                    const { text, soon } = featureOf(f);
                    return (
                      <li key={text} className="flex gap-3 text-[16px] leading-[24px]">
                        <Check className={dark ? 'text-coral' : 'text-blue'} />
                        <span>{text} {soon && <SoonTag dark={dark} />}</span>
                      </li>
                    );
                  })}
                </ul>
              </article>
            );
          })}
        </div>
        <p className="mt-6 text-[14px] text-ink-muted">Prices in US dollars.</p>
      </div>
    </section>
  );
}
